import styled from "styled-components";
import { AnimateAppearanceWrapper } from "../../../../../components/animate-appearence/AnimateAppearanceWrapper";
import { Button, PageTitle } from "../../../../../components/ui-kit";
import { Spinner } from "../../../../../components/ui-kit/spinner/Spinner";
import { PromotionCodesTable } from "../components/PromotionCodesTable";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../../constants/routes";
import { useGetPromotionCodes } from "../hooks/useGetPromotionCodes";
import { useAuthUserContext } from "../../../../../context/AdministratorContextProvider";

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Card = styled.div`
  margin-top: 20px;
  background-color: #fff;
  padding: 24px;
  border: 1px solid #eeeeee;
  border-radius: 10px;
`;

const ToolBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  padding: 8px 14px;
  border: 1px solid #dbe3eb;
  border-radius: 6px;
  outline: none;
  width: 260px;
  &:focus {
    border-color: #4f46e5;
  }
`;

const PaginationBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: #495b6c;
`;

const PageButton = styled.button<{ $disabled?: boolean }>`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #dbe3eb;
  background: ${(p) => (p.$disabled ? "#f8f9fa" : "#fff")};
  color: ${(p) => (p.$disabled ? "#adb5bd" : "#495b6c")};
  cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
  transition: all 0.15s;
  &:hover {
    ${(p) =>
      !p.$disabled &&
      "background: #f3f4f6; border-color: #4f46e5; color: #4f46e5;"}
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 0;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: #8181a5;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const PromotionCodesList: React.FunctionComponent = () => {
  const { authUser } = useAuthUserContext();
  const navigate = useNavigate();
  const {
    promoCodes,
    isLoading,
    hasMore,
    hasPrev,
    page,
    goToNextPage,
    goToPrevPage,
    search,
    setSearch,
  } = useGetPromotionCodes();

  const goToCreate = () => {
    navigate(AppRoutes.Private.Subscription.CREATE_PROMOTION_CODE);
  };

  return (
    <AnimateAppearanceWrapper>
      <Header>
        <PageTitle title="Promotion codes" />
        {authUser.policies?.actionsOnSystemSettings ? (
          <Button onClick={goToCreate}>Create promotion code</Button>
        ) : null}
      </Header>
      <Card>
        <ToolBar>
          <SearchInput
            placeholder="Search by exact code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </ToolBar>

        {isLoading ? (
          <LoadingWrap>
            <Spinner size={40} />
          </LoadingWrap>
        ) : promoCodes.length > 0 ? (
          <>
            <PromotionCodesTable data={promoCodes} />
            <PaginationBar>
              <span>Page {page}</span>
              <PageButton
                $disabled={!hasPrev}
                onClick={() => hasPrev && goToPrevPage()}
              >
                Previous
              </PageButton>
              <PageButton
                $disabled={!hasMore}
                onClick={() => hasMore && goToNextPage()}
              >
                Next
              </PageButton>
            </PaginationBar>
          </>
        ) : (
          <EmptyState>
            {search
              ? "No promotion codes match your search."
              : "No promotion codes available."}
          </EmptyState>
        )}
      </Card>
    </AnimateAppearanceWrapper>
  );
};

export default PromotionCodesList;
